import { BoatBySlugQueryResult, BoatCeClassificationItem, BoatDesigner, BoatEngine } from '@app/sanity.types';
import { Anchor, Flex, Tooltip } from 'antd';
import { BadgeInfo, ChevronDown } from 'lucide-react';
import Link from 'next/link';
import type { ReactElement } from 'react';
import UnitDetail from './UnitDetail';

export type BoatOverviewMenuProps = {
  title: string;
  desc: string | string[] | number | ReactElement;
  path?: string | string[];
};

type BoatDesignerType = Pick<BoatDesigner, '_id' | 'name' | 'website'>;

function isValidHttpUrl(string) {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'http:' || url.protocol === 'https:';
}

const getValidUrl = (site?: string) => {
  if (site && site.length > 3) {
    if (isValidHttpUrl(site)) {
      return site;
    }

    return 'https://' + site;
  }

  return null;
};

export const BoatOverviewMenu = (boat: BoatBySlugQueryResult, jumbotronHeight: number): BoatOverviewMenuProps[] => {
  const typeName = boat?.type?.name ?? '-';
  const typePath = boat?.type?.slug?.current ? `/boats/type/${boat.type.slug.current}` : undefined;
  const makerName = boat?.manufacturer?.name ?? '-';
  const makerPath = boat?.manufacturer?.slug?.current ? `/maker/${boat.manufacturer.slug.current}` : undefined;
  const hullTypeName = boat?.hullType?.name ?? '-';
  const hullTypePath = boat?.hullType?.slug?.current ? `/boats/hull-type/${boat.hullType.slug.current}` : undefined;
  const hullMaterialName = boat?.hullMaterial?.name ?? '-';

  const categoryLinks = (boat?.categories ?? []).reduce<{ name: string; path: string }[]>((acc, item) => {
    if (!item?.name || !item?.slug?.current) {
      return acc;
    }

    acc.push({
      name: item.name,
      path: `/boats/category/${item?.slug?.current}`,
    });

    return acc;
  }, []);

  const categoryDesc = categoryLinks.length > 0 ? categoryLinks.map((item) => ` ${item.name}`) : '-';
  const categoryPath = categoryLinks.length > 0 ? categoryLinks.map((item) => item.path) : undefined;

  return [
    {
      title: 'Type',
      desc: typeName,
      path: typePath,
    },
    {
      title: 'Maker',
      desc: makerName,
      path: makerPath,
    },
    {
      title: 'Hull type',
      desc: hullTypeName,
      path: hullTypePath,
    },
    {
      title: 'Hull Material',
      desc: hullMaterialName,
    },
    // {
    //   title: f('activity'),
    //   desc: boat?.activities.name,
    // },
    {
      title: 'Category',
      desc: categoryDesc,
      path: categoryPath,
    },
    {
      title: 'Boat length (LOA)',
      desc: <UnitDetail value={boat.loa as number} unit="ft" />,
    },
    {
      title: null,
      desc: (
        <Anchor
          affix={false}
          targetOffset={jumbotronHeight}
          className="single-public-boat-see-all"
          items={[
            {
              key: 'single-public-boat-content',
              href: '#single-public-boat-content',
              title: (
                <Flex align="center">
                  <span>See all details</span> <ChevronDown size={16} />
                </Flex>
              ),
            },
          ]}
        />
      ),
    },
  ];
};

/**
 * Keys on an engine object that are not spec data and must never reach the table.
 *
 * `_type` is the culprit behind the "boatEngine" row: Sanity stamps every array member with its
 * schema name, so `_type` carries the literal string `'boatEngine'`. The render loop only skipped
 * `__typename`/`id`/`powerType`/`_key`, so `_type` was emitted as a row of its own — and because
 * `isEmptyEngine` also counted `_key`/`_type` as content, an engine entry with nothing filled in
 * was still considered non-empty, leaving "boatEngine" as its only visible row.
 *
 * `powerType` is excluded because it is folded into the `power` row rather than shown on its own,
 * and on its own it does not make an engine non-empty.
 */
const ENGINE_NON_SPEC_KEYS = new Set(['_key', '_type', '__typename', 'id', 'powerType']);

/** CMS values are lowercase (`inboard`); the table shows the schema's titles. */
const ENGINE_TYPE_LABELS: Record<string, string> = {
  inboard: 'Inboard',
  outboard: 'Outboard',
};

const getEngineTypeLabel = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.trim()) {
    return null;
  }

  // Unknown values fall through unchanged rather than being dropped, in case older documents
  // carry a spelling the schema no longer lists.
  return ENGINE_TYPE_LABELS[value.trim().toLowerCase()] ?? value;
};

const formatEnginePower = (power: unknown, powerType?: string): string | null => {
  if (typeof power !== 'string' && typeof power !== 'number') {
    return null;
  }

  const parsed = parseFloat(`${power}`);

  if (Number.isNaN(parsed)) {
    return null;
  }

  return [parsed.toLocaleString(undefined, { maximumFractionDigits: 2 }), powerType?.toLocaleUpperCase()]
    .filter(Boolean)
    .join(' ');
};

const getEngineValue = (item: BoatEngine, key: string): BoatTableSubMenuItem['value'] => {
  const raw = (item as Record<string, unknown>)[key];

  if (key === 'type') {
    return getEngineTypeLabel(raw);
  }

  if (key === 'power') {
    return formatEnginePower(raw, item.powerType);
  }

  return typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean' ? raw : null;
};

function isEmptyEngine(item: BoatEngine): boolean {
  return Object.entries(item).every(([key, value]) => ENGINE_NON_SPEC_KEYS.has(key) || !value);
}

/**
 * `boatTank.capacity` is free text in the CMS ("100 gal", "150 L", "50"), so a bare number is
 * treated as gallons and converted like every other measurement, while a value that already
 * carries its own unit is shown verbatim rather than mangled by a conversion it does not fit.
 */
const renderTankCapacity = (capacity: unknown): string | ReactElement | null => {
  if (typeof capacity !== 'string' || !capacity.trim()) {
    return null;
  }

  const trimmed = capacity.trim();
  const numeric = Number(trimmed);

  return Number.isFinite(numeric) ? <UnitDetail value={numeric} unit="gallon" /> : trimmed;
};

export type BoatTableSubMenuItem = {
  key: string;
  name: string | ReactElement;
  hasValue: boolean;
  value: string | number | boolean | ReactElement | ReactElement[] | null;
  empty?: boolean;
};

export type BoatTableMenuItem = {
  title: string;
  link: string;
  subMenu: BoatTableSubMenuItem[];
};

export type BoatTableProps = {
  title: string;
  link: string;
  menu: BoatTableMenuItem[];
}[];

const BOAT_TABLE_TRANS: Record<string, Record<string, string>> = {
  title: {
    specs: 'Specs',
    performance_and_ratios: 'Performance & Ratios',
  },
  menu: {
    main: 'Main',
    general: 'General',
    designer: 'Designer',
    rig_and_sails: 'Rig & Sails',
    engine: 'Engine',
    storage: 'Storage',
    detail: 'Detail',
  },
  subMenu: {
    maker: 'Maker',
    maker_website: 'Maker website',
    model: 'Model',
    first_built_hull: 'First built hull',
    last_built_hull: 'Last built hull',
    version: 'Version',
    price: 'Price',
    boat_type: 'Boat type',
    category: 'Category',
    hull_type: 'Hull type',
    hull_material: 'Hull material',
    cabins: 'Cabins',
    births: 'Births',
    heads: 'Heads',
    top_speed: 'Top speed',
    cruising_speed: 'Cruising speed',
    mast_height: 'Mast height',
    length_overall: 'Length overall',
    length_at_waterline: 'Length at waterline',
    beam_overall: 'Beam overall',
    beam_center_ine: 'Beam center-line',
    beam_hull: 'Beam hull',
    headroom: 'Headroom',
    bridgedeck_clearance: 'Bridgedeck clearance',
    nominal_bridgedeck_clearance: 'Nominal bridgedeck clearance',
    draft_maximum: 'Draft maximum',
    draft_minimum: 'Draft minimum',
    displacement: 'Displacement',
    tonnage_gross: 'Tonnage Gross',
    tonnage_net: 'Tonnage Net',
    ballast_weight: 'Ballast weight',
    ballast_type: 'Ballast type',
    helm: 'Helm',
    rudder: 'Rudder',
    'hull_appendages.title': 'Hull appendages',
    hull_appendages: 'Hull appendages {letterCount}',
    cockpit: 'Cockpit',
    unsinkable: 'Unsinkable',
    trailerable: 'Trailerable',
    'ce_classification.title': 'CE classification',
    'ce_classification.html':
      ' The CE design category indicates the ability to cope with certain weather conditions (the sailboat is designed for these conditions) and usually presented as the amount of persons able to be safely aboard: <span><li>A: Wind &lt; &nbsp;9, Waves &lt; 10m &nbsp; </li><li>B: Wind &lt; &nbsp;8, Waves &lt; 8m&nbsp;</li><li>B: Wind &lt; &nbsp;6, Waves &lt; 4m&nbsp;</li><li>B: Wind &lt; &nbsp;4, Waves &lt; 0.5m&nbsp;</li></span>',
    ce_classification_a: 'CE classification A',
    ce_classification_b: 'CE classification B',
    ce_classification_c: 'CE classification C',
    ce_classification_d: 'CE classification D',
    name_studio: 'Name/Studio',
    website: 'Website',
    rotating_spars: 'Rotating spars',
    mast: 'Mast',
    spars: 'Spars',
    standing_rigging: 'Standing rigging',
    number_of_spreaders: 'Number of spreaders',
    spreaders_angle: 'Spreaders angle',
    upwind_sail_area: 'Upwind sail area',
    downwind_sail_area: 'Downwind sail area',
    'mainsail.title': 'Mainsail',
    mainsail: 'Mainsail {letterCount}',
    'headsail.title': 'Headsail',
    'headsail.html': 'Staysail, Genoa, Jib',
    headsail: 'Headsail {letterCount}',
    'spinnaker.title': 'Spinnaker',
    'spinnaker.html': 'Gennaker',
    spinnaker: 'Spinnaker A',
    engine: 'Engine {letterCount}',
    'engine.maker': 'Maker',
    'engine.website': 'Website',
    'engine.model': 'Model',
    'engine.type': 'Type',
    'engine.drive': 'Drive',
    'engine.fuel': 'Fuel',
    'engine.power': 'Power',
    'engine.power_type': 'Power Type',
    'fuel_tank.title': 'Fuel tank',
    fuel_tank: 'Fuel tank {letterCount}',
    'water_tank.title': 'Water tank',
    water_tank: 'Water tank {letterCount}',
    'holding_tank.title': 'Holding tank',
    holding_tank: 'Holding tank {letterCount}',
    'water_heater.title': 'Water heater',
    water_heater: 'Water heater {letterCount}',
    'sail_area_to_displacement.title': 'Sail Area to Displacement (SA/D)',
    'sail_area_to_displacement.html':
      "The ratio sail area to displacement is obtained by dividing the sail area by the boat's displaced volume to the power two-thirds. The ratio sail area to displacement can be used to compare the relative sail plan of different sailboats no matter what their size. Upwind: under 18 the ratio indicates a cruise oriented sailboat with limited performances especially in light wind, while over 25 it indicates a fast sailboat.",
    upwind_sa_to_displacement: 'Upwind SA to Displacement',
    downwind_sa_to_displacement: 'Downwind SA to Displacement',
    'hull_displacement_speed.title': 'Hull Displacement Speed',
    'hull_displacement_speed.html':
      "As a ship moves in the water, it creates standing waves that oppose its movement. This effect increases dramatically the resistance when the boat reaches a speed-length ratio (speed-length ratio is the ratio between the speed in knots and the square root of the waterline length in feet) of about 1.2 (corresponding to a Froude Number of 0.35) . This very sharp rise in resistance, between speed-length ratio of 1.2 to 1.5, is insurmountable for heavy sailboats and so becomes an apparent barrier. This leads to the concept of 'hull speed'. The hull speed is obtained by multiplying the square root of the waterline length (in feet) by 1.34.",
    'displacement_to_length.title': 'Displacement to Length (D/L)',
    'displacement_to_length.html':
      " The lower a boat's Displacement/Length (LWL) ratio, the less power it takes to drive the boat to its nominal hull speed<li>less than 100 = Ultralight</li> <li>100-200 = Light</li> <li>200-275 = Moderate</li> <li>275-350 = Heavy</li><li>350+ = Ultraheavy</li>",
    'ballast_to_displacement.title': 'Ballast to Displacement (B/D) ',
    'ballast_to_displacement.html':
      "The Ballast ratio is an indicator of stability; it is obtained by dividing the boat's displacement by the mass of the ballast. Since the stability depends also of the hull shapes and the position of the center of gravity, only the boats with similar ballast arrangements and hull shapes should be compared. The higher the ballast ratio is, the greater is the stability.",
    'capsize_screening_formula.title': 'Capsize Screening Formula (CSF)',
    'capsize_screening_formula.html':
      'Designed to determine if a boat has blue water capability. The CSF compares beam with displacement since excess beam contributes to capsize and heavy displacement reduces capsize vulnerability. The boat is better suited for ocean passages (vs coastal cruising) if the result of the calculation is 2.0 or less. The lower the better.',
    stability_speed: 'Stability Speed',
    kelsall_stability_index: 'Kelsall Stability Index (KSI)',
  },
};

export const BoatTable = (boat: BoatBySlugQueryResult): BoatTableProps => {
  const getCeClassificationValue = (item?: BoatCeClassificationItem): number | boolean | null => {
    if (!item?.has) {
      return null;
    }

    return item.level ?? true;
  };

  const getLetterCountTrans = (index: number): string =>
    'A|B|C|D|E|F|G|H|I|J|K|L|M|N|O|P|Q|R|S|T|U|V|W|X|Y|Z'.split('|')[index];

  const f = (key: string, id: string, letterIndex?: number): string => {
    const value = BOAT_TABLE_TRANS[key]?.[id] ?? id;

    if (letterIndex !== undefined) {
      return value.replace('{letterCount}', getLetterCountTrans(letterIndex));
    }

    return value;
  };

  const mapSimpleRow = (key: string, rowKey: string, itemKey: string, transId: string): BoatTableSubMenuItem[] => {
    if (boat[key] && boat[key].length !== 0) {
      return boat[key].map((item: any, index: number) => ({
        key: `${rowKey}-${index}`,
        name: f('subMenu', transId, index),
        // Read the same field that is rendered — `item.value` was hardcoded, so any row projecting
        // a different field reported itself as empty and got hidden.
        hasValue: !!item[itemKey],
        value: item[itemKey],
      }));
    }

    return [
      {
        key: `${rowKey}-default`,
        hasValue: false,
        name: f('subMenu', transId, 0),
        value: null,
      },
    ];
  };

  const mapTankRow = (
    tanks: Array<{ capacity?: string }> | null | undefined,
    rowKey: string,
    transId: string,
  ): BoatTableSubMenuItem[] => {
    if (!tanks?.length) {
      return [
        {
          key: `${rowKey}-default`,
          hasValue: false,
          name: f('subMenu', transId, 0),
          value: null,
        },
      ];
    }

    return tanks.map((tank, index) => {
      const value = renderTankCapacity(tank?.capacity);

      return {
        key: `${rowKey}-${index}`,
        name: f('subMenu', transId, index),
        hasValue: value !== null,
        value,
      };
    });
  };

  /** Header row that labels a group of tank rows. */
  const tankGroupHeader = (rowKey: string, transId: string): BoatTableSubMenuItem => ({
    key: rowKey,
    hasValue: false,
    name: f('subMenu', transId),
    empty: true,
    value: null,
  });

  const mapEngineRow = (): BoatTableSubMenuItem[] => {
    const items: BoatTableSubMenuItem[] = [];

    const reusedTransId = 'single_boat.table.sub_menu.title.engine';

    if (boat.engines?.length > 0) {
      boat.engines.forEach((item, index) => {
        if (isEmptyEngine(item)) {
          return;
        }

        items.push({
          key: `${reusedTransId}-${index}`,
          name: f('subMenu', 'engine', index),
          empty: true,
          hasValue: true,
          value: null,
        });

        for (const key in item) {
          if (!ENGINE_NON_SPEC_KEYS.has(key)) {
            const value = getEngineValue(item, key);

            items.push({
              key: `specs-engine-${key}-${index}`,
              hasValue: !!value,
              name: f('subMenu', `engine.${key}`),
              value,
            });
          }
        }
      });
    }

    return items;
  };

  const mapDesigners = (designers: BoatDesignerType[], spec: string) => {
    const items: BoatTableSubMenuItem[] = [];

    if (designers?.length) {
      designers.map((designer, idx) => {
        const { name, website } = designer;

        const url = getValidUrl(website);

        items.push({
          key: `specs-${spec + name}-name-studio-${idx}`,
          hasValue: !!name,
          name: f('subMenu', 'name_studio'),
          value: name,
        });

        items.push({
          key: `specs-${spec + website}-website-${idx}`,
          hasValue: !!url,
          name: f('subMenu', 'website'),
          value: url && (
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
          ),
        });
      });
    }

    return items;
  };

  const toolTipClassName = 'single-public-boat-tooltip';

  return [
    {
      title: f('title', 'specs'),
      link: 'specs-table',
      menu: [
        {
          title: f('menu', 'main'),
          link: 'specs-main',
          subMenu: [
            {
              key: 'specs-main-maker',
              hasValue: !!boat?.manufacturer?.name,
              name: f('subMenu', 'maker'),
              value: <Link href={`/maker/${boat?.manufacturer?.slug?.current}`}>{boat?.manufacturer?.name}</Link>,
            },
            {
              key: 'specs-main-maker-website',
              hasValue: !!getValidUrl(boat?.manufacturer?.website),
              name: f('subMenu', 'maker_website'),
              value: getValidUrl(boat?.manufacturer?.website) && (
                <a href={getValidUrl(boat?.manufacturer?.website)} target="_blank" rel="noreferrer">
                  {getValidUrl(boat?.manufacturer?.website)}
                </a>
              ),
            },
            {
              key: 'specs-main-model',
              hasValue: !!boat.model,
              name: f('subMenu', 'model'),
              value: boat?.model,
            },
            {
              key: 'specs-main-first-built-hull',
              hasValue: !!boat.yearFirst,
              name: f('subMenu', 'first_built_hull'),
              value: boat.yearFirst,
            },
            {
              key: 'specs-main-last-built-hull',
              hasValue: !!boat.yearLast,
              name: f('subMenu', 'last_built_hull'),
              value: boat.yearLast,
            },
            {
              key: 'specs-main-version',
              hasValue: !!boat.version,
              name: f('subMenu', 'version'),
              value: boat.version,
            },
            {
              key: 'specs-main-price',
              hasValue: boat.referencePrice?.amount ? true : false,
              name: f('subMenu', 'price'),
              value:
                boat?.referencePrice?.amount &&
                `${boat?.referencePrice?.amount?.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${
                  boat?.referencePrice?.currency
                }`,
            },
            {
              key: 'specs-main-boat-type',
              hasValue: boat?.type?.name ? true : false,
              name: f('subMenu', 'boat_type'),
              value: <Link href={`/boats/type/${boat?.type?.slug?.current}`}>{boat?.type?.name}</Link>,
            },
            {
              key: 'specs-main-category',
              hasValue: boat?.categories?.length > 0,
              name: f('subMenu', 'category'),
              value: boat?.categories?.map((item) => (
                <Link href={`/boats/category/${item?.slug?.current}`} key={item._id} style={{ marginRight: '8px' }}>
                  {item.name}
                </Link>
              )),
            },
            // {
            //   key: 'specs-main-activity',
            //   hasValue: boat.activities.name ? true : false,
            //   name: f('subMenu', 'activity'),
            //   value: boat.activities.name,
            // },
            {
              key: 'specs-main-hull-type',
              hasValue: !!boat.hullType?.name,
              name: f('subMenu', 'hull_type'),
              value: <Link href={`/boats/hull-type/${boat.hullType?.slug?.current}`}>{boat.hullType?.name}</Link>,
            },
            {
              key: 'specs-main-hull-material',
              hasValue: boat?.hullMaterial?.name ? true : false,
              name: f('subMenu', 'hull_material'),
              value: (
                <Link href={`/boats/hull-type/${boat?.hullMaterial?.slug?.current}`}>{boat?.hullMaterial?.name}</Link>
              ),
            },
            {
              key: 'specs-main-cabins',
              hasValue: !!boat.cabins,
              name: f('subMenu', 'cabins'),
              value: boat.cabins,
            },
            {
              key: 'specs-main-births',
              hasValue: !!boat.berths,
              name: f('subMenu', 'births'),
              value: boat.berths,
            },
            {
              key: 'specs-main-heads',
              hasValue: !!boat.heads,
              name: f('subMenu', 'heads'),
              value: boat.heads,
            },
            // {
            //   key: 'specs-main-notes',
            //   hasValue: boat?.notes ? true : false,
            //   name: 'Notes',
            //   value: boat?.notes ? <p dangerouslySetInnerHTML={{ __html: `${boat?.notes}` }} /> : '-',
            // },
          ],
        },
        {
          title: f('menu', 'general'),
          link: 'specs-general',
          subMenu: [
            {
              key: 'specs-general-top-speed',
              name: f('subMenu', 'top_speed'),
              hasValue: !!boat.topSpeed,
              value: <UnitDetail value={boat.topSpeed} unit="mph" />,
            },
            {
              key: 'specs-general-cruising-speed',
              name: f('subMenu', 'cruising_speed'),
              hasValue: !!boat.cruisingSpeed,
              value: <UnitDetail value={boat.cruisingSpeed} unit="mph" />,
            },
            {
              key: 'specs-general-mast-height',
              hasValue: !!boat.mast,
              name: f('subMenu', 'mast_height'),
              value: <UnitDetail value={boat.mast} unit="ft" />,
            },
            {
              key: 'specs-general-length-overall',
              hasValue: !!boat.loa,
              name: f('subMenu', 'length_overall'),
              value: <UnitDetail value={boat.loa as number} unit="ft" />,
            },
            {
              key: 'specs-general-length-at-waterline',
              hasValue: !!boat.lwl,
              name: f('subMenu', 'length_at_waterline'),
              value: <UnitDetail value={boat.lwl} unit="ft" />,
            },
            {
              key: 'specs-general-beam-overall',
              hasValue: !!boat.boa,
              name: f('subMenu', 'beam_overall'),
              value: <UnitDetail value={boat.boa} unit="ft" />,
            },
            {
              key: 'specs-general-beam-center-line',
              hasValue: !!boat.bcl,
              name: f('subMenu', 'beam_center_ine'),
              value: <UnitDetail value={boat.bcl} unit="ft" />,
            },
            {
              key: 'specs-general-beam-hull',
              hasValue: !!boat.bh,
              name: f('subMenu', 'beam_hull'),
              value: <UnitDetail value={boat.bh} unit="ft" />,
            },
            {
              key: 'specs-general-headroom',
              hasValue: !!boat.headroom,
              name: f('subMenu', 'headroom'),
              value: <UnitDetail value={boat.headroom} unit="ft" />,
            },
            {
              key: 'specs-general-bridgedeck-clearance',
              hasValue: !!boat.bdcl,
              name: f('subMenu', 'bridgedeck_clearance'),
              value: <UnitDetail value={boat.bdcl} unit="ft" />,
            },
            {
              key: 'specs-general-nominal-bridgedeck-clearance',
              hasValue: !!boat.nom,
              name: f('subMenu', 'nominal_bridgedeck_clearance'),
              value: <UnitDetail value={boat.nom} unit="ft" />,
            },
            {
              key: 'specs-general-draft-maximum',
              hasValue: !!boat.draftMax,
              name: f('subMenu', 'draft_maximum'),
              value: <UnitDetail value={boat.draftMax} unit="ft" />,
            },
            {
              key: 'specs-general-draft-minimum',
              hasValue: !!boat.draftMin,
              name: f('subMenu', 'draft_minimum'),
              value: <UnitDetail value={boat.draftMin} unit="ft" />,
            },
            {
              key: 'specs-general-displacement',
              hasValue: !!boat.displacement,
              name: f('subMenu', 'displacement'),
              value: <UnitDetail value={boat.displacement} unit="lb" />,
            },
            {
              key: 'specs-general-tonnage-gross',
              hasValue: !!boat.tonnageGross,
              name: f('subMenu', 'tonnage_gross'),
              value: <UnitDetail value={boat.tonnageGross} unit="lt" />,
            },
            {
              key: 'specs-general-tonnage-net',
              hasValue: !!boat.tonnageNet,
              name: f('subMenu', 'tonnage_net'),
              value: <UnitDetail value={boat.tonnageNet} unit="lt" />,
            },
            {
              key: 'specs-general-ballast-weight',
              hasValue: !!boat.ballastWeight,
              name: f('subMenu', 'ballast_weight'),
              value: <UnitDetail value={boat.ballastWeight} unit="lb" />,
            },
            {
              key: 'specs-general-ballast-type',
              hasValue: !!boat.ballastType,
              name: f('subMenu', 'ballast_type'),
              value: boat.ballastType,
            },
            {
              key: 'specs-general-helm',
              hasValue: !!boat.helm,
              name: f('subMenu', 'helm'),
              value: boat.helm,
            },
            {
              key: 'specs-general-rudder',
              hasValue: !!boat.rudder,
              name: f('subMenu', 'rudder'),
              value: boat.rudder,
            },
            {
              key: 'specs-general-hull-appendages',
              hasValue: false,
              name: f('subMenu', 'hull_appendages.title'),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('hullAppendages', 'specs-general-hull-appendages', 'value', 'hull_appendages'),
            {
              key: 'specs-general-cockpit',
              hasValue: !!boat.cockpitLocation,
              name: f('subMenu', 'cockpit'),
              value: boat.cockpitLocation,
            },
            {
              key: 'specs-general-unsinkable',
              hasValue: !!boat.unsinkable,
              name: f('subMenu', 'unsinkable'),
              value: boat.unsinkable,
            },
            {
              key: 'specs-general-trailerable',
              hasValue: !!boat.trailerable,
              name: f('subMenu', 'trailerable'),
              value: boat.trailerable,
            },
            {
              key: 'specs-general-ce-classification',
              hasValue: false,
              name: (
                <span>
                  {f('subMenu', 'ce_classification.title')}{' '}
                  <Tooltip
                    classNames={{ root: toolTipClassName }}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'ce_classification.html'),
                        }}
                      />
                    }
                  >
                    <BadgeInfo />
                  </Tooltip>
                </span>
              ),
              empty: true,
              value: null,
            },
            {
              key: 'specs-general-ce-classification-a',
              hasValue: !!boat.ceClassification?.a?.has,
              name: f('subMenu', 'ce_classification_a'),
              value: getCeClassificationValue(boat?.ceClassification?.a),
            },
            {
              key: 'specs-general-ce-classification-b',
              hasValue: !!boat?.ceClassification?.b?.has,
              name: f('subMenu', 'ce_classification_b'),
              value: getCeClassificationValue(boat?.ceClassification?.b),
            },
            {
              key: 'specs-general-ce-classification-c',
              hasValue: !!boat?.ceClassification?.c?.has,
              name: f('subMenu', 'ce_classification_c'),
              value: getCeClassificationValue(boat?.ceClassification?.c),
            },
            {
              key: 'specs-general-ce-classification-d',
              hasValue: !!boat?.ceClassification?.d?.has,
              name: f('subMenu', 'ce_classification_d'),
              value: getCeClassificationValue(boat?.ceClassification?.d),
            },
          ],
        },
        {
          title: f('menu', 'designer'),
          link: 'specs-designer',
          subMenu: [
            {
              key: 'specs-designer-navalarchitect ',
              hasValue: boat?.designers?.length ? true : false,
              name: 'Naval architect',
              empty: true,
              value: null,
            },
            ...mapDesigners(boat.designers, 'Naval architect'),
            {
              key: 'specs-designer-exterior-designer',
              hasValue: boat?.exteriorDesigners?.length ? true : false,
              name: 'Exterior designer',
              empty: true,
              value: null,
            },
            ...mapDesigners(boat.exteriorDesigners, 'Exterior designer'),
            {
              key: 'specs-designer-interior-designer',
              hasValue: boat?.interiorDesigners?.length ? true : false,
              name: 'Interior designer',
              empty: true,
              value: null,
            },
            ...mapDesigners(boat.interiorDesigners, 'Interior designer'),
          ],
        },
        {
          title: f('menu', 'rig_and_sails'),
          link: 'specs-rig-and-sails',
          subMenu: [
            // {
            //   key: 'specs-rig-and-sails-rigging',
            //   hasValue: !!boat.rigging,
            //   name: f('subMenu', 'rigging'),
            //   value: boat.rigging,
            // },
            {
              key: 'specs-rig-and-sails-rotating-spars',
              hasValue: !!boat?.rotatingSpars,
              name: f('subMenu', 'rotating_spars'),
              value: boat.rotatingSpars,
            },
            {
              key: 'specs-rig-and-sails-mast',
              hasValue: !!boat.mast,
              name: f('subMenu', 'mast'),
              value: boat.mast,
            },
            {
              key: 'specs-rig-and-sails-spars',
              hasValue: !!boat.spars,
              name: f('subMenu', 'spars'),
              value: boat.spars,
            },
            {
              key: 'specs-rig-and-sails-standing-rigging',
              hasValue: !!boat.standingRigging,
              name: f('subMenu', 'standing_rigging'),
              value: boat.standingRigging,
            },
            {
              key: 'specs-rig-and-sails-number-of-spreaders',
              hasValue: !!boat.spreadersLevel,
              name: f('subMenu', 'number_of_spreaders'),
              value: boat.spreadersLevel,
            },
            {
              key: 'specs-rig-and-sails-spreaders-angle',
              hasValue: !!boat.spreadersAngle,
              name: f('subMenu', 'spreaders_angle'),
              value: boat.spreadersAngle,
            },
            {
              key: 'specs-rig-and-sails-upwind-sail-area',
              hasValue: !!boat.upwindSailArea,
              name: f('subMenu', 'upwind_sail_area'),
              value: <UnitDetail value={boat.upwindSailArea} unit="sq.ft" />,
            },
            {
              key: 'specs-rig-and-sails-Downwind-sail-area',
              hasValue: !!boat.downwindSailArea,
              name: f('subMenu', 'downwind_sail_area'),
              value: <UnitDetail value={boat.downwindSailArea} unit="sq.ft" />,
            },
            {
              key: 'specs-rig-and-sails-mainsail',
              hasValue: false,
              name: f('subMenu', 'mainsail.title'),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('mainsail', 'specs-rig-and-sails-mainsail', 'value', 'mainsail'),
            {
              key: 'specs-rig-and-sails-headsail',
              hasValue: false,
              name: (
                <span>
                  {f('subMenu', 'headsail.title')}{' '}
                  <Tooltip
                    classNames={{ root: toolTipClassName }}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'headsail.html'),
                        }}
                      />
                    }
                  >
                    <BadgeInfo />
                  </Tooltip>
                </span>
              ),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('headsail', 'specs-rig-and-sails-headsail', 'value', 'headsail'),
            {
              key: 'specs-rig-and-sails-spinnaker',
              hasValue: false,
              name: (
                <span>
                  {f('subMenu', 'spinnaker.title')}{' '}
                  <Tooltip
                    classNames={{ root: toolTipClassName }}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'spinnaker.html'),
                        }}
                      />
                    }
                  >
                    <BadgeInfo />
                  </Tooltip>
                </span>
              ),
              empty: true,
              value: null,
            },
            ...mapSimpleRow('spinnaker', 'specs-rig-and-sails-spinnaker', 'value', 'spinnaker'),
          ],
        },
        {
          title: f('menu', 'engine'),
          link: 'specs-engine',
          subMenu: [...mapEngineRow()],
        },
        {
          title: f('menu', 'storage'),
          link: 'specs-storage',
          subMenu: [
            tankGroupHeader('specs-storage-fuel-tank', 'fuel_tank.title'),
            ...mapTankRow(boat.fuelTanks, 'specs-storage-fuel-tank', 'fuel_tank'),
            tankGroupHeader('specs-storage-water-tank', 'water_tank.title'),
            ...mapTankRow(boat.waterTanks, 'specs-storage-water-tank', 'water_tank'),
            // Fuel and water are the primary tanks and always get a row, so a boat with none still
            // shows them as empty. Holding tanks and water heaters are rare in the CMS, so they are
            // only listed for the boats that actually carry them rather than sitting empty on every
            // other boat — including when "Hide empty fields" is unchecked.
            ...(boat.holdingTanks?.length
              ? [
                  tankGroupHeader('specs-storage-holding-tank', 'holding_tank.title'),
                  ...mapTankRow(boat.holdingTanks, 'specs-storage-holding-tank', 'holding_tank'),
                ]
              : []),
            ...(boat.waterHeaters?.length
              ? [
                  tankGroupHeader('specs-storage-water-heater', 'water_heater.title'),
                  ...mapTankRow(boat.waterHeaters, 'specs-storage-water-heater', 'water_heater'),
                ]
              : []),
          ],
        },
      ],
    },
    {
      title: f('title', 'performance_and_ratios'),
      link: 'performance-and-ratios-table',
      menu: [
        {
          title: f('menu', 'detail'),
          link: 'performance-and-ratios-detail',
          subMenu: [
            {
              key: 'performance-and-ratios-detail-sail-area-to-displacement',
              hasValue: !!boat.sailAreaDisplacement,
              name: (
                <span>
                  {f('subMenu', 'sail_area_to_displacement.title')}{' '}
                  <Tooltip
                    classNames={{ root: toolTipClassName }}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'sail_area_to_displacement.html'),
                        }}
                      />
                    }
                  >
                    <BadgeInfo />
                  </Tooltip>
                </span>
              ),
              value: boat.sailAreaDisplacement?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-upwind-sa-to-displacement',
              hasValue: !!boat.upwindSailAreaDisplacement,
              name: f('subMenu', 'upwind_sa_to_displacement'),
              value: boat.upwindSailAreaDisplacement?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-downwind-sa-to-displacement',
              hasValue: !!boat.downwindSailAreaDisplacement,
              name: f('subMenu', 'downwind_sa_to_displacement'),
              value: boat.downwindSailAreaDisplacement?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-hull-displacement-speed',
              hasValue: !!boat.displacementHullSpeed,
              name: (
                <span>
                  {f('subMenu', 'hull_displacement_speed.title')}{' '}
                  <Tooltip
                    classNames={{ root: toolTipClassName }}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'hull_displacement_speed.html'),
                        }}
                      />
                    }
                  >
                    <BadgeInfo />
                  </Tooltip>
                </span>
              ),
              value: boat.displacementHullSpeed?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-displacement-to-length',
              hasValue: !!boat.displacementLength,
              name: (
                <span>
                  {f('subMenu', 'displacement_to_length.title')}{' '}
                  <Tooltip
                    classNames={{ root: toolTipClassName }}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'displacement_to_length.html'),
                        }}
                      />
                    }
                  >
                    <BadgeInfo />
                  </Tooltip>
                </span>
              ),
              value: boat.displacementLength?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-ballast-to-displacement',
              hasValue: !!boat.ballastDisplacement,
              name: (
                <span>
                  {f('subMenu', 'ballast_to_displacement.title')}{' '}
                  <Tooltip
                    classNames={{ root: toolTipClassName }}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'ballast_to_displacement.html'),
                        }}
                      />
                    }
                  >
                    <BadgeInfo />
                  </Tooltip>
                </span>
              ),
              value: boat.ballastDisplacement?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-capsize-screening-formula',
              hasValue: !!boat.csf,
              name: (
                <span>
                  {f('subMenu', 'capsize_screening_formula.title')}{' '}
                  <Tooltip
                    classNames={{ root: toolTipClassName }}
                    title={
                      <div
                        dangerouslySetInnerHTML={{
                          __html: f('subMenu', 'capsize_screening_formula.html'),
                        }}
                      />
                    }
                  >
                    <BadgeInfo />
                  </Tooltip>
                </span>
              ),
              value: boat.csf?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
            {
              key: 'performance-and-ratios-detail-stability-speed',
              hasValue: false,
              name: f('subMenu', 'stability_speed'),
              value: null,
            },
            {
              key: 'performance-and-ratios-detail-kelsall-stability-index',
              hasValue: !!boat.ksi,
              name: f('subMenu', 'kelsall_stability_index'),
              value: boat.ksi?.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            },
          ],
        },
      ],
    },
  ];
};
